import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, Flower, Sun, Drop, CheckCircle, Quotes, ShieldCheck, HandHeart, Sparkle, Users, Globe, WhatsappLogo, VideoCamera } from "@phosphor-icons/react";
import Reveal from "../components/Reveal";
import { specialities, pillars, facilities } from "../data/site";
import { api } from "../lib/api";
import { useImage, useMedia } from "../lib/media";
import { useTreatments } from "../lib/treatments";
import useSeo from "../lib/useSeo";
import { useT, usePick } from "../lib/i18n";
import ExploreInDepth from "../components/ExploreInDepth";

const pillarIcons = { "Herbal Rasayanas": Leaf, "Vajikarana": Flower, "Diet (Ahara)": Drop, "Lifestyle (Vihara)": Sun };

export default function Home() {
  useSeo({
    title: "Yuva Ayurvedic Clinic Prayagraj | Ayurvedic Treatment Since 2008",
    absoluteTitle: true,
    description: "Yuva Ayurvedic Clinic Private Limited — Prayagraj's most trusted Ayurvedic clinic for sexual health. 18+ years experience. 50,000+ satisfied patients. 100% confidential. Natural treatment, no side effects. Book now.",
    keywords: "Ayurvedic clinic Prayagraj, Gupt rog specialist Prayagraj, Prayagraj mein mardana kamzori ka Ayurvedic ilaaj, Mardana shakti badhane ka natural tarika, Shighrapatan ka ilaaj Prayagraj, Napunsakta ka Ayurvedic ilaaj, Best sex clinic Prayagraj, Ayurvedic treatment for erectile dysfunction India, Confidential sexual health treatment India, NRI men's health treatment Prayagraj",
  });
  const t = useT();
  const pick = usePick();
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);
  const heroImg = useImage("hero", "https://images.unsplash.com/photo-1768209198274-01dd8d79cd1e?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80");
  const { map: mediaMap } = useMedia();
  const { items: dynamicTreatments } = useTreatments();
  const list = dynamicTreatments.length > 0 ? dynamicTreatments : specialities;

  useEffect(() => {
    api.get("/testimonials").then(r => setTestimonials(r.data)).catch(() => {});
    api.get("/blog").then(r => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative -mt-20 pt-20 min-h-[92vh] overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-16 grid md:grid-cols-12 gap-10 items-center relative z-10">
          <div className="md:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="overline">
              {t("hero.overline")}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.1 }}
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl xl:text-[92px] leading-[0.95] tracking-tight mt-6"
            >
              {t("hero.title.l1")}<br/>
              {t("hero.title.l2")}<br/>
              <span className="italic text-terracotta">{t("hero.title.l3")}</span><br/>
              {t("hero.title.l4")}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.35 }}
              className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed"
            >
              {t("hero.subtitle")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/book-appointment"
                data-testid="hero-book-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-forest text-cream px-8 py-4 hover:bg-terracotta transition-all duration-500 hover:-translate-y-1"
              >
                {t("cta.book")}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/treatments"
                data-testid="hero-explore-cta"
                className="inline-flex items-center gap-2 text-forest/80 hover:text-terracotta transition-colors underline underline-offset-8 decoration-1 decoration-border"
              >
                {t("cta.explore")}
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.8 }}
              className="mt-16 flex items-center gap-8 text-xs uppercase tracking-[0.22em] text-muted-foreground"
            >
              <span>{t("hero.stat.iso")}</span>
              <span className="w-6 h-px bg-border"/>
              <span>{t("hero.stat.patients")}</span>
              <span className="w-6 h-px bg-border"/>
              <span>{t("hero.stat.since")}</span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 relative"
          >
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-ivory relative">
              <img src={heroImg} alt="Private Ayurvedic consultation room at Yuva Ayurvedic Clinic Prayagraj — specialist men's sexual wellness clinic in Uttar Pradesh, India" className="w-full h-full object-cover" loading="eager" decoding="async" fetchpriority="high"/>
              <div className="absolute inset-0 bg-gradient-to-t from-forest/20 to-transparent"/>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-cream border border-border/50 rounded-2xl p-5 shadow-sm max-w-[230px] hidden md:block">
              <div className="overline">{t("hero.promise.label")}</div>
              <p className="font-serif-display text-xl mt-2 leading-tight">{t("hero.promise.text")}</p>
            </div>
          </motion.div>
        </div>

        {/* decorative word */}
        <div aria-hidden className="absolute -bottom-8 left-0 right-0 text-center pointer-events-none">
          <span className="font-serif-display italic text-[22vw] leading-none text-sage/10 whitespace-nowrap">ayurveda</span>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 lg:py-32" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-16">
          <Reveal className="md:col-span-5">
            <div className="overline">{t("story.overline")}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1] mt-4">
              {t("story.title.l1")}<br/>{t("story.title.l2")} <span className="italic text-terracotta">{t("story.title.l3")}</span><br/>{t("story.title.l4")}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6 md:col-start-7">
            <p className="text-lg leading-relaxed text-foreground/85">
              {t("story.p1")}
            </p>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              {t("story.p2")}
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-border pt-8">
              <div><div className="font-serif-display text-3xl">18+</div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">{t("story.stat.years")}</div></div>
              <div><div className="font-serif-display text-3xl">50,000+</div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">{t("story.stat.patients")}</div></div>
              <div><div className="font-serif-display text-3xl">100%</div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">{t("story.stat.discretion")}</div></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Prayagraj spiritual heritage — SEO + international Kumbh Mela / Sangam narrative */}
      <section className="py-24 lg:py-32 bg-forest text-cream relative overflow-hidden" data-testid="heritage-section">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06] flex items-center justify-center">
          <span className="font-serif-display italic text-[18vw] leading-none whitespace-nowrap">Sangam</span>
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline text-sage">Rooted in Prayagraj</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1]">
              The spiritual<br/>capital of India.<br/><span className="italic text-terracotta/90">A wellness heritage.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7">
            <p className="text-lg leading-relaxed text-cream/90">
              Prayagraj — ancient <em>Prayāga</em> — is the sacred confluence of the Ganga, Yamuna and mythical Saraswati at the <strong>Triveni Sangam</strong>. Every twelve years, millions of seekers arrive here for the <strong>Kumbh Mela</strong> — the largest spiritual gathering on earth.
            </p>
            <p className="mt-5 text-cream/80 leading-relaxed">
              This is also one of the oldest continuous homes of the <strong>Vedic traditions</strong> that gave the world Ayurveda. Yuva Ayurvedic Clinic Private Limited is part of this living heritage — an <strong>Ayurvedic men's sexual wellness clinic in Prayagraj</strong> that welcomes patients from across India and international travellers visiting the Sangam or Kumbh Mela for confidential, classical Ayurvedic care.
            </p>
            <ul className="mt-8 grid sm:grid-cols-3 gap-6 text-sm">
              <li className="border-t border-cream/20 pt-4">
                <div className="font-serif-display text-2xl">Kumbh Mela</div>
                <div className="mt-1 text-cream/70">UNESCO-recognised spiritual congregation, held at the Sangam.</div>
              </li>
              <li className="border-t border-cream/20 pt-4">
                <div className="font-serif-display text-2xl">Triveni Sangam</div>
                <div className="mt-1 text-cream/70">Sacred confluence of three rivers — the heart of Prayagraj.</div>
              </li>
              <li className="border-t border-cream/20 pt-4">
                <div className="font-serif-display text-2xl">Vedic Roots</div>
                <div className="mt-1 text-cream/70">The soil where classical Ayurveda was first codified.</div>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Specialities bento */}
      <section className="py-24 lg:py-32 bg-ivory/50" data-testid="specialities-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <Reveal>
              <div className="overline">{t("specs.overline")}</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-2xl leading-[1]">
                {t("specs.title.l1")}<br/><span className="italic text-terracotta">{t("specs.title.l2")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="max-w-md text-muted-foreground">
                {t("specs.subtitle")}
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6 lg:gap-8">
            {list.map((s, i) => {
              const img = mediaMap[`spec_${s.slug}`] || s.image;
              return (
              <Reveal
                key={s.slug}
                delay={i * 0.06}
                className={`${
                  i === 0 ? "md:col-span-3 md:row-span-2" :
                  i === 1 ? "md:col-span-3" :
                  i === 2 ? "md:col-span-2" :
                  i === 3 ? "md:col-span-2" :
                  i === 4 ? "md:col-span-2" :
                  "md:col-span-3"
                }`}
              >
                <Link
                  to="/treatments"
                  data-testid={`speciality-${s.slug}`}
                  className="group relative block h-full min-h-[260px] overflow-hidden rounded-2xl bg-ivory border border-border/50"
                >
                  <img src={img} alt={`${pick(s, "title")} — confidential Ayurvedic treatment for men at Yuva Ayurvedic Clinic Prayagraj`} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-80 transition-all duration-700" loading="lazy" decoding="async"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/30 to-transparent"/>
                  <div className="relative h-full p-7 flex flex-col justify-end text-cream">
                    <div className="overline text-sage/90">Speciality</div>
                    <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 leading-tight">{pick(s, "title")}</h3>
                    <p className="mt-3 text-sm text-cream/80 max-w-sm">{pick(s, "short")}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-cream/90 group-hover:text-terracotta transition-colors">
                      Read more <ArrowRight size={14}/>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );})}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 lg:py-32" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="overline text-center">{t("pillars.overline")}</div>
            <h2 className="font-serif-display text-center text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1]">
              {t("pillars.title.l1")} <span className="italic text-terracotta">{t("pillars.title.l2")}</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6 lg:gap-8">
            {pillars.map((p, i) => {
              const Icon = pillarIcons[p.title] || Leaf;
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  <div className="bg-ivory border border-border/50 rounded-2xl p-8 h-full hover:shadow-sm transition-all duration-500 hover:-translate-y-1" data-testid={`pillar-${p.title.toLowerCase()}`}>
                    <Icon size={32} weight="duotone" className="text-sage"/>
                    <h3 className="font-serif-display text-2xl mt-5">{p.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust pillars — 5 reasons patients choose us */}
      <section className="py-24 lg:py-32 bg-ivory/60" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="overline">{t("trust.overline")}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl leading-[1]">
              {t("trust.title.l1")} <span className="italic text-terracotta">{t("trust.title.l2")}</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-4">
            {[
              { icon: ShieldCheck, k: "p1" },
              { icon: HandHeart, k: "p2" },
              { icon: Users, k: "p3" },
              { icon: Sparkle, k: "p4" },
              { icon: Globe, k: "p5" },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.k} delay={i * 0.06}>
                  <div className="bg-cream border border-border/50 rounded-2xl p-7 h-full hover:border-terracotta/60 transition-colors" data-testid={`trust-${p.k}`}>
                    <div className="w-11 h-11 rounded-full bg-sage/15 text-sage flex items-center justify-center">
                      <Icon size={20} weight="duotone"/>
                    </div>
                    <h3 className="font-serif-display text-xl mt-5 leading-tight">{t(`trust.${p.k}.h`)}</h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{t(`trust.${p.k}.p`)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hinglish SEO section — "Prayagraj Mein Bharosemand Ayurvedic Ilaaj"
          Targets locals searching in Hindi typed in English letters (Hinglish). */}
      <section className="py-24 lg:py-32" data-testid="hindi-seo-section" lang="hi-Latn">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline">{t("hindi.overline")}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1]">
              {t("hindi.title")}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7">
            <ul className="space-y-3">
              {["hindi.bullet1","hindi.bullet2","hindi.bullet3","hindi.bullet4","hindi.bullet5"].map((k) => (
                <li key={k} className="flex items-start gap-3 text-base sm:text-lg leading-relaxed text-foreground/90 py-2 border-b border-border/40">
                  <CheckCircle size={20} weight="duotone" className="text-sage mt-1 shrink-0"/>
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://wa.me/919519078666?text=Namaste%20Yuva%20Ayurvedic%2C%20mujhe%20consultation%20book%20karni%20hai."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hindi-cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 hover:-translate-y-0.5 transition-all duration-500"
              >
                <WhatsappLogo size={18} weight="fill"/> {t("hindi.cta.whatsapp")}
              </a>
              <Link
                to="/book-appointment"
                data-testid="hindi-cta"
                className="inline-flex items-center gap-3 rounded-full bg-forest text-cream px-7 py-3.5 hover:bg-terracotta transition-all duration-500 hover:-translate-y-0.5"
              >
                {t("hindi.cta.book")} <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Deep-dive SEO pages — boosts internal-link signal for each condition-specific landing page */}
      <ExploreInDepth
        heading="Explore treatment in depth"
        subheading="Every men's private concern we treat has its own dedicated page — with symptoms, causes, our classical Ayurvedic protocol and answers to the most common patient questions."
      />

      {/* NRI / International online consultation */}
      <section className="py-24 lg:py-32 bg-terracotta/10 relative overflow-hidden" data-testid="nri-section">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04] flex items-center justify-center">
          <Globe size={520} weight="thin" className="text-terracotta" aria-hidden="true"/>
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5">
            <div className="overline">{t("nri.overline")}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1]">
              {t("nri.title.l1")} <span className="italic text-terracotta">{t("nri.title.l2")}</span>
            </h2>
            <p className="mt-8 max-w-md text-foreground/85 leading-relaxed">
              {t("nri.sub")}
            </p>
            <div className="mt-6 text-xs uppercase tracking-[0.22em] text-terracotta" data-testid="nri-countries">
              {t("nri.countries")}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7">
            <div className="bg-cream border border-border/50 rounded-2xl p-8 lg:p-10">
              <ul className="space-y-4">
                {[
                  { icon: VideoCamera, k: "nri.bullet1" },
                  { icon: Globe, k: "nri.bullet2" },
                  { icon: Sparkle, k: "nri.bullet3" },
                  { icon: WhatsappLogo, k: "nri.bullet4" },
                ].map((row, i) => {
                  const Icon = row.icon;
                  return (
                    <li key={row.k} className="flex items-start gap-4 py-2">
                      <div className="w-10 h-10 rounded-full bg-sage/15 text-sage flex items-center justify-center shrink-0">
                        <Icon size={18} weight="duotone"/>
                      </div>
                      <span className="pt-2 text-[15px]">{t(row.k)}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="https://wa.me/919519078666?text=Hello%20Yuva%20Ayurvedic,%20I%20am%20writing%20from%20outside%20India%20and%20would%20like%20to%20book%20an%20online%20consultation."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nri-whatsapp"
                  className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-3 text-sm hover:bg-terracotta transition-all"
                >
                  <WhatsappLogo size={16} weight="fill"/> {t("nri.cta.whatsapp")}
                </a>
                <Link
                  to="/book-appointment"
                  data-testid="nri-book"
                  className="inline-flex items-center gap-2 rounded-full border border-forest text-forest px-6 py-3 text-sm hover:bg-forest hover:text-cream transition-all"
                >
                  <VideoCamera size={16} weight="duotone"/> {t("nri.cta.book")}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="py-20" data-testid="consult-cta-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="bg-terracotta text-cream rounded-2xl p-12 lg:p-20 relative overflow-hidden">
            <div className="relative z-10 grid md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-8">
                <div className="overline text-cream/80">{t("consultCta.overline")}</div>
                <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1]">
                  {t("consultCta.title.l1")}<br/>{t("consultCta.title.l2")}<br/><span className="italic">{t("consultCta.title.l3")}</span>
                </h2>
                <p className="mt-6 max-w-xl text-cream/85">
                  {t("consultCta.subtitle")}
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link
                  to="/book-appointment"
                  data-testid="cta-book-consultation"
                  className="inline-flex items-center gap-3 rounded-full bg-cream text-forest px-8 py-4 hover:bg-forest hover:text-cream transition-all duration-500 hover:-translate-y-1"
                >
                  {t("cta.bookShort")} <ArrowRight size={18}/>
                </Link>
              </div>
            </div>
            <div aria-hidden className="absolute -bottom-16 -right-8 font-serif-display italic text-[18vw] text-cream/10 leading-none pointer-events-none">consult</div>
          </div>
        </div>
      </section>

      {/* Testimonials marquee */}
      {testimonials.length > 0 && (
        <section className="py-24 lg:py-32 overflow-hidden" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mb-16">
            <Reveal>
              <div className="overline">Patient voices</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl leading-[1]">
                What our patients <span className="italic text-terracotta">say.</span>
              </h2>
            </Reveal>
          </div>
          <div className="mask-fade-x">
            <div className="marquee-track">
              {[...testimonials, ...testimonials].map((t, i) => (
                <article
                  key={i}
                  className="w-[440px] shrink-0 bg-ivory border border-border/50 rounded-2xl p-8"
                  data-testid={`testimonial-${i}`}
                >
                  <Quotes size={28} weight="fill" className="text-terracotta/80"/>
                  <p className="font-serif-display text-2xl leading-snug mt-4 italic">“{t.quote}”</p>
                  <div className="mt-6 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{t.author}</div>
                      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-0.5">{t.location}</div>
                    </div>
                    <div className="text-terracotta tracking-[0.3em]">{"★".repeat(t.rating)}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Facilities */}
      <section className="py-24 lg:py-32 bg-forest text-cream" data-testid="facilities-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline text-sage">{t("facilities.overline")}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1]">
              {t("facilities.title.l1")}<br/>{t("facilities.title.l2")} <span className="italic text-terracotta">{t("facilities.title.l3")}</span>
            </h2>
          </Reveal>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {facilities.map((f, i) => (
              <Reveal key={f} delay={i * 0.05}>
                <div className="flex items-center gap-4 py-4 border-b border-cream/10" data-testid={`facility-${i}`}>
                  <CheckCircle size={20} weight="duotone" className="text-sage shrink-0"/>
                  <span className="text-cream/90">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Blog preview */}
      {posts.length > 0 && (
        <section className="py-24 lg:py-32" data-testid="journal-preview-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
              <Reveal>
                <div className="overline">{t("journal.overline")}</div>
                <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1]">{t("journal.title.l1")} <span className="italic text-terracotta">{t("journal.title.l2")}</span></h2>
              </Reveal>
              <Link to="/blog" className="inline-flex items-center gap-2 text-forest/80 hover:text-terracotta underline underline-offset-8 decoration-1 decoration-border" data-testid="journal-view-all">
                {t("cta.viewAll")} <ArrowRight size={16}/>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {posts.slice(0, 3).map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <Link to={`/blog/${p.slug}`} className="group block" data-testid={`journal-preview-${i}`}>
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-ivory">
                      <img src={p.cover_image} alt={`${p.title} — Yuva Ayurvedic Clinic Prayagraj journal`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async"/>
                    </div>
                    <div className="mt-5">
                      <div className="overline">{p.category}</div>
                      <h3 className="font-serif-display text-2xl mt-2 leading-snug group-hover:text-terracotta transition-colors">{pick(p, "title")}</h3>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{pick(p, "excerpt")}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
